import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Clock, Send, Sparkles, X, Trash2, ImageIcon, Shuffle, BookOpen,
  ChevronLeft, MapPin, User, Loader2, Sun, ChevronRight, Volume2, VolumeX,
  Map, Users, Camera, Download,
} from 'lucide-react';

import CinematicWalkthroughSection from './CinematicWalkthroughSection';
import HistoryBookSection from './HistoryBookSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PresetCard({ p, onPick, disabled }) {
  return (
    <button
      onClick={() => onPick(p)}
      disabled={disabled}
      className="neo-card p-3 text-left bg-white hover:bg-[#FFE8F5] transition disabled:opacity-50 flex flex-col gap-1.5"
      data-testid={`tt-preset-${p.key}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-2xl">{p.emoji}</span>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-sm leading-tight truncate">{p.title}</h4>
          <p className="text-[11px] opacity-60">{p.era} · {p.character}</p>
        </div>
      </div>
      <p className="text-[11px] opacity-75 leading-snug line-clamp-2">{p.hint}</p>
    </button>
  );
}


// ============== Selfie helper ==============
async function compressSelfie(file, maxSize = 800) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          if (width > height) { height = (height * maxSize) / width; width = maxSize; }
          else { width = (width * maxSize) / height; height = maxSize; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ============== Selfie scene generator + gallery ==============
function SelfieSceneSection({ presets }) {
  const [selfie, setSelfie] = useState(null);
  const [presetKey, setPresetKey] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [extra, setExtra] = useState('');
  const [gallery, setGallery] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);

  const loadGallery = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/timetravel/selfie-gallery`, { withCredentials: true });
      setGallery(data?.items || []);
    } catch { /* */ }
  }, []);

  useEffect(() => { loadGallery(); }, [loadGallery]);

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { setSelfie(await compressSelfie(file)); }
    catch { toast.error('Kunde inte läsa bilden'); }
  };

  const generate = async () => {
    if (!selfie) { toast.error('Ladda upp en selfie först 📸'); return; }
    if (!presetKey && !customPrompt.trim()) { toast.error('Välj en epok eller skriv egen scen'); return; }
    setGenerating(true);
    try {
      const payload = {
        selfie_base64: selfie,
        extra_notes: extra.trim() || null,
      };
      if (presetKey) payload.preset_key = presetKey;
      else payload.custom_prompt = customPrompt.trim();

      const { data } = await axios.post(`${API}/timetravel/selfie-scene`, payload, { withCredentials: true });
      toast.success('Tidsresa-bild klar! ✨');
      setViewing(data.selfie_scene);
      loadGallery();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte skapa bilden');
    } finally {
      setGenerating(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Ta bort denna tidsresa-bild?')) return;
    try {
      await axios.delete(`${API}/timetravel/selfie-gallery/${id}`, { withCredentials: true });
      loadGallery();
      if (viewing?.id === id) setViewing(null);
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const downloadImage = (item) => {
    const a = document.createElement('a');
    a.href = `data:${item.mime_type || 'image/png'};base64,${item.image_base64}`;
    a.download = `tidsresa_${item.id}.png`;
    a.click();
  };

  return (
    <div className="neo-card p-4 bg-gradient-to-br from-[#FFE8F5] via-[#FFFAF0] to-[#E8F4FF]" data-testid="tt-selfie-section">
      <div className="flex items-center gap-2 mb-2">
        <Camera className="w-4 h-4 text-[#D06892]" />
        <h3 className="font-['Fredoka'] font-bold text-base">Sätt in dig själv i scenen 📸</h3>
      </div>
      <p className="text-xs opacity-70 mb-3">
        Ladda upp en selfie + välj epok → AI sätter in dig i den historiska scenen, klädd i tidens kläder. Som de virala "I time-traveled"-videorna 🕰️✨
      </p>

      {/* Selfie upload */}
      <div className="mb-3">
        <p className="text-[10px] font-bold uppercase opacity-60 mb-1">1. Din selfie</p>
        {selfie ? (
          <div className="relative inline-block">
            <img src={selfie} alt="Selfie" className="w-32 h-32 object-cover rounded-lg border-2 border-black/10" />
            <button onClick={() => setSelfie(null)} className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow" data-testid="tt-selfie-remove">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()}
            className="w-full sm:w-auto neo-btn px-4 py-3 bg-white border-dashed text-sm flex items-center gap-2"
            data-testid="tt-selfie-upload">
            <Camera className="w-4 h-4" /> Välj foto från ditt galleri
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" onChange={pickPhoto} className="hidden" />
      </div>

      {/* Era picker */}
      <div className="mb-3">
        <p className="text-[10px] font-bold uppercase opacity-60 mb-1">2. Välj epok</p>
        <select value={presetKey} onChange={(e) => { setPresetKey(e.target.value); if (e.target.value) setCustomPrompt(''); }}
          className="neo-input w-full text-sm" data-testid="tt-selfie-preset-select">
          <option value="">— Välj en epok (eller skriv egen nedan) —</option>
          {presets.map((p) => (
            <option key={p.key} value={p.key}>{p.emoji} {p.title}</option>
          ))}
        </select>
      </div>

      {/* Custom prompt */}
      <div className="mb-3">
        <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Eller egen scen</p>
        <input value={customPrompt}
          onChange={(e) => { setCustomPrompt(e.target.value); if (e.target.value) setPresetKey(''); }}
          placeholder="Ex: 'I en café i Paris 1925' eller 'En badort på 50-talet'"
          className="neo-input w-full text-sm" data-testid="tt-selfie-custom-prompt" />
      </div>

      {/* Extras */}
      <div className="mb-3">
        <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Extra detaljer (valfritt)</p>
        <input value={extra} onChange={(e) => setExtra(e.target.value)}
          placeholder="Ex: 'med en liten hund', 'vinterscen', 'glad min'"
          className="neo-input w-full text-sm" data-testid="tt-selfie-extra" />
      </div>

      <button onClick={generate} disabled={generating || !selfie || (!presetKey && !customPrompt.trim())}
        className="neo-btn w-full py-3 bg-[#FFC8DD] font-bold text-base flex items-center justify-center gap-2"
        data-testid="tt-selfie-generate">
        {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Skapar din tidsresa... (10-20s)</> : <><Sparkles className="w-4 h-4" /> Sätt in mig i scenen ✨</>}
      </button>

      {/* Gallery */}
      {gallery.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] font-bold uppercase opacity-60 mb-2">Dina tidsresa-bilder ({gallery.length})</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="tt-selfie-gallery">
            {gallery.map((item) => (
              <div key={item.id} className="relative group cursor-pointer" onClick={() => setViewing(item)} data-testid={`tt-selfie-item-${item.id}`}>
                <img src={`data:${item.mime_type || 'image/png'};base64,${item.image_base64}`}
                  alt={item.title}
                  className="w-full aspect-square object-cover rounded-lg border-2 border-black/10 group-hover:border-[#D06892] transition" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-white p-2 text-[10px] font-bold rounded-b-lg truncate">
                  {item.title}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Viewer modal */}
      {viewing && (
        <div className="fixed inset-0 z-[125] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3"
             onClick={() => setViewing(null)} data-testid="tt-selfie-viewer">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-3 max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase opacity-60">{viewing.era}</p>
                <h4 className="font-['Fredoka'] font-bold text-base">{viewing.title}</h4>
                {viewing.location && <p className="text-[11px] opacity-70">📍 {viewing.location}</p>}
              </div>
              <button onClick={() => setViewing(null)} className="p-1.5 hover:bg-black/5 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <img src={`data:${viewing.mime_type || 'image/png'};base64,${viewing.image_base64}`}
                 alt={viewing.title}
                 className="w-full rounded-lg" data-testid="tt-selfie-viewer-image" />
            <div className="flex gap-2">
              <button onClick={() => downloadImage(viewing)}
                className="neo-btn flex-1 py-2 bg-[#B9FBC0] text-sm font-bold flex items-center justify-center gap-1"
                data-testid="tt-selfie-download">
                <Download className="w-4 h-4" /> Ladda ner
              </button>
              <button onClick={() => deleteItem(viewing.id)}
                className="neo-btn px-3 py-2 bg-white text-sm font-bold text-red-600"
                data-testid="tt-selfie-delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function HomeView({ presets, onStart, onRandom, journeys, onOpen, onDelete, loading }) {
  const [desc, setDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const startFromDesc = async () => {
    if (!desc.trim()) { toast.error('Skriv vart du vill resa'); return; }
    setCreating(true);
    await onStart({ description: desc.trim() });
    setCreating(false);
  };

  return (
    <div className="space-y-4" data-testid="tt-home">
      <header className="neo-card p-5 bg-gradient-to-br from-[#E8F0FF] via-[#FFE8F5] to-[#FDF0D5] relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="font-['Fredoka'] font-bold text-xl flex items-center gap-2">
            <Clock className="w-5 h-5" /> Färdas i tiden
          </h2>
          <p className="text-sm text-black/75 mt-1 max-w-xl">
            Beskriv vart du vill — eller välj en epok. Du möter en person därifrån, kan prata med dem på svenska, och be om scenbilder. Upptäck nya världar 🕰️
          </p>
        </div>
        <div className="absolute -right-4 -bottom-4 text-7xl opacity-10">🕰️</div>
      </header>

      {/* Free description */}
      <div className="neo-card p-4 bg-white" data-testid="tt-free-desc">
        <h3 className="font-bold text-sm mb-2 flex items-center gap-1.5">
          <Sparkles className="w-4 h-4" /> Berätta vart du vill
        </h3>
        <textarea
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="Ex: 'Jag vill stå på Christoffer Columbus däck när han siktar Amerika' eller 'En medeltida riddarborg i Skåne'..."
          rows={3}
          className="neo-input w-full text-sm"
          maxLength={400}
          data-testid="tt-desc-input"
        />
        <div className="flex gap-2 mt-2">
          <button
            onClick={startFromDesc}
            disabled={creating || !desc.trim()}
            className="neo-btn flex-1 py-2 bg-[#FFC8DD] font-bold text-sm flex items-center justify-center gap-1 disabled:opacity-60"
            data-testid="tt-start-desc"
          >
            {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Bygger resan...</> : <><Send className="w-4 h-4" /> Starta tidsresan</>}
          </button>
          <button
            onClick={onRandom}
            className="neo-btn px-3 py-2 bg-[#C8B6FF] font-bold text-sm flex items-center gap-1"
            data-testid="tt-random"
            title="Slumpa en hemlig epok"
          >
            <Shuffle className="w-4 h-4" /> Hemlig epok
          </button>
        </div>
      </div>

      {/* Presets */}
      <div>
        <h3 className="font-bold text-sm mb-2 flex items-center gap-1.5">
          <BookOpen className="w-4 h-4" /> Eller välj en epok
        </h3>
        <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-3" data-testid="tt-presets">
          {presets.map((p) => (
            <PresetCard key={p.key} p={p} onPick={(pp) => onStart({ preset_key: pp.key })} disabled={creating} />
          ))}
        </div>
      </div>

      {/* NEW: Selfie scene generator — put user IN the scene */}
      <SelfieSceneSection presets={presets} />

      {/* NEW: Cinematic walkthrough — full movie-like experience */}
      <CinematicWalkthroughSection />

      {/* NEW: History book — chapter summaries of all walkthroughs */}
      <HistoryBookSection />

      {/* Past journeys */}
      <div>
        <h3 className="font-bold text-sm mb-2 flex items-center gap-1.5">
          <Clock className="w-4 h-4" /> Dina tidsresor
        </h3>
        {loading ? (
          <p className="text-sm opacity-60">Laddar...</p>
        ) : journeys.length === 0 ? (
          <div className="neo-card p-4 bg-white text-center text-sm opacity-70">
            Inga sparade resor än — välj en epok ovan för att starta din första 💛
          </div>
        ) : (
          <div className="grid gap-2 grid-cols-1 md:grid-cols-2" data-testid="tt-journeys-list">
            {journeys.map((j) => (
              <div key={j.id} className="neo-card p-3 bg-white flex items-start gap-2" data-testid={`tt-journey-${j.id}`}>
                <button
                  onClick={() => onOpen(j.id)}
                  className="flex-1 text-left flex items-start gap-2 min-w-0"
                >
                  <span className="text-2xl shrink-0">{j.emoji || '🕰️'}</span>
                  <div className="min-w-0">
                    <h4 className="font-bold text-sm leading-tight truncate">{j.title}</h4>
                    <p className="text-[11px] opacity-60 truncate">{j.era} · {j.character}</p>
                    <p className="text-[10px] opacity-50 mt-0.5">
                      {j.message_count} meddelanden · {j.scene_count} bilder
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => onDelete(j.id)}
                  className="neo-btn p-1.5 bg-white text-red-600"
                  aria-label="Ta bort"
                  data-testid={`tt-journey-delete-${j.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DayInLifeView({ journey, onClose, onUpdated }) {
  const [chapters, setChapters] = useState(journey.day_in_life?.chapters || null);
  const [loading, setLoading] = useState(!journey.day_in_life);
  const [idx, setIdx] = useState(0);
  const [sceneCache, setSceneCache] = useState({});
  const [generatingScene, setGeneratingScene] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/timetravel/journeys/${journey.id}/day-in-life`,
        {}, { withCredentials: true }
      );
      setChapters(data.day_in_life?.chapters || []);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte berätta dagen');
    } finally {
      setLoading(false);
    }
  }, [journey.id, onUpdated]);

  useEffect(() => {
    if (!chapters) generate();
  }, [chapters, generate]);

  // Pre-fill cached scene images from journey.scenes
  useEffect(() => {
    if (!chapters || !journey.scenes) return;
    const map = {};
    chapters.forEach((c) => {
      if (c.scene_image_id) {
        const s = (journey.scenes || []).find((sc) => sc.id === c.scene_image_id);
        if (s) map[c.key] = s;
      }
    });
    setSceneCache((prev) => ({ ...prev, ...map }));
  }, [chapters, journey.scenes]);

  const speakNarration = (text) => {
    if (!('speechSynthesis' in window)) {
      toast.error('Uppläsning stöds inte i denna webbläsare');
      return;
    }
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const u = new window.SpeechSynthesisUtterance(text);
    u.lang = 'sv-SE';
    u.rate = 0.95;
    u.pitch = 1.0;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  // Cancel speech when navigating
  const goTo = (newIdx) => {
    if (speaking) { window.speechSynthesis.cancel(); setSpeaking(false); }
    setIdx(newIdx);
  };

  const generateChapterScene = async () => {
    if (!chapters || !chapters[idx]) return;
    const ch = chapters[idx];
    setGeneratingScene(true);
    try {
      const { data } = await axios.post(
        `${API}/timetravel/journeys/${journey.id}/day-in-life/${ch.key}/scene`,
        {}, { withCredentials: true }
      );
      setSceneCache((prev) => ({ ...prev, [ch.key]: data.scene }));
      // mark chapter locally
      setChapters((prev) => prev.map((c) =>
        c.key === ch.key ? { ...c, scene_image_id: data.scene.id } : c
      ));
      toast.success('🎨 Scenen är ritad');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte skapa scen');
    } finally {
      setGeneratingScene(false);
    }
  };

  if (loading || !chapters) {
    return (
      <div className="space-y-3" data-testid="tt-dol-loading">
        <header className="neo-card p-3 bg-gradient-to-br from-[#FFF7B5] to-[#FFE8F5] flex items-center gap-2">
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="tt-dol-close">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <Sun className="w-4 h-4" />
          <h3 className="font-bold text-sm">En dag i livet med {journey.character}</h3>
        </header>
        <div className="neo-card p-8 bg-white text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-[#FFC8DD]" />
          <p className="text-sm font-bold">{journey.character} berättar sin dag...</p>
          <p className="text-xs opacity-60 mt-1">Detta tar 5-15 sekunder</p>
        </div>
      </div>
    );
  }

  const current = chapters[idx];
  const sceneObj = sceneCache[current.key];
  const totalChapters = chapters.length;

  return (
    <div className="space-y-3" data-testid="tt-dol-view">
      <header className="neo-card p-3 bg-gradient-to-br from-[#FFF7B5] to-[#FFE8F5] flex items-center gap-2">
        <button onClick={onClose} className="neo-btn p-1.5 bg-white" aria-label="Tillbaka" data-testid="tt-dol-close">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xl">{journey.emoji || '🕰️'}</span>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm leading-tight truncate">En dag i livet med {journey.character}</h3>
          <p className="text-[11px] opacity-70 truncate">{journey.era} · {journey.location}</p>
        </div>
      </header>

      {/* Chapter dots */}
      <div className="flex gap-2 justify-center" data-testid="tt-dol-dots">
        {chapters.map((c, i) => (
          <button
            key={c.key}
            onClick={() => goTo(i)}
            className={`neo-btn px-2 py-1 text-[11px] font-bold transition ${
              i === idx ? 'bg-[#FFC8DD]' : i < idx ? 'bg-[#B9FBC0]' : 'bg-white'
            }`}
            data-testid={`tt-dol-dot-${c.key}`}
            title={c.label}
          >
            <span className="mr-1">{c.emoji}</span>{c.label}
          </button>
        ))}
      </div>

      {/* Chapter card */}
      <div className="neo-card p-4 bg-white" data-testid={`tt-dol-chapter-${current.key}`}>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2">
            <span className="text-2xl">{current.emoji}</span> {current.label}
          </h4>
          <span className="text-[10px] opacity-60 font-bold">Kapitel {idx + 1} / {totalChapters}</span>
        </div>

        {/* Scene image (if generated) */}
        {sceneObj ? (
          <div className="mb-3">
            <img
              src={`data:${sceneObj.mime_type || 'image/png'};base64,${sceneObj.image_base64}`}
              alt={sceneObj.description || current.label}
              className="w-full rounded-xl border-2 border-black"
            />
          </div>
        ) : (
          <button
            onClick={generateChapterScene}
            disabled={generatingScene}
            className="w-full mb-3 neo-btn py-2 bg-[#B9FBC0] font-bold text-sm flex items-center justify-center gap-1 disabled:opacity-60"
            data-testid="tt-dol-generate-scene"
          >
            {generatingScene
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Ritar {current.label.toLowerCase()}...</>
              : <><ImageIcon className="w-4 h-4" /> Visa hur {current.label.toLowerCase()} ser ut</>}
          </button>
        )}

        {/* Narration */}
        <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="tt-dol-narration">
          {current.narration}
        </div>

        {/* TTS */}
        <button
          onClick={() => speakNarration(current.narration)}
          className={`mt-3 neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${speaking ? 'bg-[#FFC8DD]' : 'bg-white'}`}
          data-testid="tt-dol-tts"
        >
          {speaking
            ? <><VolumeX className="w-3.5 h-3.5" /> Stoppa uppläsning</>
            : <><Volume2 className="w-3.5 h-3.5" /> Lyssna ({journey.character})</>}
        </button>
      </div>

      {/* Nav buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => goTo(Math.max(0, idx - 1))}
          disabled={idx === 0}
          className="neo-btn flex-1 py-2 bg-white font-bold text-sm flex items-center justify-center gap-1 disabled:opacity-40"
          data-testid="tt-dol-prev"
        >
          <ChevronLeft className="w-4 h-4" /> Föregående
        </button>
        <button
          onClick={() => goTo(Math.min(totalChapters - 1, idx + 1))}
          disabled={idx >= totalChapters - 1}
          className="neo-btn flex-1 py-2 bg-[#FFC8DD] font-bold text-sm flex items-center justify-center gap-1 disabled:opacity-40"
          data-testid="tt-dol-next"
        >
          Nästa <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Ambient particle overlay for cinematic mode
function AmbientParticles({ kind }) {
  // Pre-compute particles once per kind
  const particles = React.useMemo(() => {
    const count = kind === 'stars' ? 60 : kind === 'snow' ? 40 : kind === 'embers' ? 30 : kind === 'petals' ? 25 : kind === 'sand' ? 50 : 40;
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 6 + Math.random() * 8,
      size: 2 + Math.random() * 4,
      drift: (Math.random() - 0.5) * 80,
    }));
  }, [kind]);

  if (!kind || kind === 'none') return null;

  const particleStyle = (p) => {
    const base = {
      position: 'absolute',
      left: `${p.left}%`,
      top: '-10%',
      width: `${p.size}px`,
      height: `${p.size}px`,
      animationDelay: `${p.delay}s`,
      animationDuration: `${p.duration}s`,
      ['--drift']: `${p.drift}px`,
    };
    if (kind === 'stars') {
      return { ...base, background: 'rgba(255,255,255,0.85)', borderRadius: '50%',
        boxShadow: '0 0 4px rgba(255,255,255,0.6)', animation: `tt-twinkle ${p.duration}s ease-in-out infinite`,
        top: `${Math.random() * 100}%` };
    }
    if (kind === 'snow') {
      return { ...base, background: 'rgba(255,255,255,0.7)', borderRadius: '50%',
        boxShadow: '0 0 3px rgba(255,255,255,0.5)', animation: `tt-fall-drift ${p.duration}s linear infinite` };
    }
    if (kind === 'embers') {
      return { ...base, background: 'rgba(255,180,80,0.85)', borderRadius: '50%',
        boxShadow: '0 0 6px rgba(255,150,50,0.7)', animation: `tt-rise ${p.duration}s ease-in infinite`, top: '110%' };
    }
    if (kind === 'petals') {
      return { ...base, width: '6px', height: '6px', background: 'rgba(255,200,221,0.75)', borderRadius: '50% 0 50% 0',
        animation: `tt-fall-drift ${p.duration}s linear infinite` };
    }
    if (kind === 'sand') {
      return { ...base, background: 'rgba(238,210,160,0.45)', borderRadius: '50%',
        animation: `tt-drift-h ${p.duration}s linear infinite`, top: `${Math.random() * 100}%` };
    }
    if (kind === 'mist') {
      return { ...base, width: '120px', height: '40px', background: 'rgba(255,255,255,0.06)',
        borderRadius: '50%', filter: 'blur(20px)',
        animation: `tt-drift-h ${p.duration}s ease-in-out infinite`, top: `${Math.random() * 100}%` };
    }
    // dust default
    return { ...base, background: 'rgba(255,235,180,0.4)', borderRadius: '50%',
      animation: `tt-fall-drift ${p.duration}s linear infinite` };
  };

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid={`tt-ambient-${kind}`}>
      {particles.map((p) => (
        <span key={p.id} style={particleStyle(p)} />
      ))}
    </div>
  );
}

function CinematicNpcChat({ journeyId, journey, location, npc, sceneObj, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [portrait, setPortrait] = useState(null);
  const [generatingPortrait, setGeneratingPortrait] = useState(false);
  const [autoTts, setAutoTts] = useState(true);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const scrollRef = useRef(null);
  const lastSpokenIdxRef = useRef(-1);

  // Strip stage-direction asterisks for TTS
  const speakLine = (text, idx) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    // Remove *...*  lines for cleaner speech
    const clean = text.replace(/\*[^*]+\*/g, '').trim();
    if (!clean) return;
    const u = new window.SpeechSynthesisUtterance(clean);
    u.lang = 'sv-SE';
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx((s) => (s === idx ? null : s));
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setSpeakingIdx(null);
  };

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/timetravel/journeys/${journeyId}/locations/${location.key}/npcs/${npc.key}/messages`,
        { withCredentials: true }
      );
      setMessages(data.messages || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [journeyId, location.key, npc.key]);

  // Load portrait if cached
  useEffect(() => {
    const cacheKey = `${location.key}::${npc.key}`;
    const cached = (journey.portraits || {})[cacheKey];
    if (cached) setPortrait(cached);
  }, [location.key, npc.key, journey.portraits]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, sending]);

  // Auto-TTS new replies
  useEffect(() => {
    if (!autoTts || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last.role === 'assistant' && messages.length - 1 > lastSpokenIdxRef.current) {
      lastSpokenIdxRef.current = messages.length - 1;
      // small delay so UI animates first
      setTimeout(() => speakLine(last.text, messages.length - 1), 250);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, autoTts]);

  // Cleanup speech on unmount
  useEffect(() => () => { if ('speechSynthesis' in window) window.speechSynthesis.cancel(); }, []);

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);
    setMessages((prev) => [...prev, { role: 'user', text, at: new Date().toISOString() }]);
    try {
      const { data } = await axios.post(
        `${API}/timetravel/journeys/${journeyId}/locations/${location.key}/npcs/${npc.key}/message`,
        { text }, { withCredentials: true }
      );
      setMessages((prev) => [...prev, data.messages_added[1]]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte svara');
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const generatePortrait = async () => {
    setGeneratingPortrait(true);
    try {
      const { data } = await axios.post(
        `${API}/timetravel/journeys/${journeyId}/locations/${location.key}/npcs/${npc.key}/portrait`,
        {}, { withCredentials: true }
      );
      setPortrait(data.portrait);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte skapa porträtt');
    } finally {
      setGeneratingPortrait(false);
    }
  };

  // Render a single message: split stage-direction and speech
  const renderAssistant = (text) => {
    // Match leading *...* on its own line(s) followed by speech
    const m = text.match(/^\s*\*([^*]+)\*\s*\n*(.*)$/s);
    if (m) {
      return (
        <>
          <div className="text-[12px] italic opacity-80 mb-1.5 text-amber-100">
            <span className="opacity-60">·</span> {m[1].trim()} <span className="opacity-60">·</span>
          </div>
          {m[2].trim() && <div>{m[2].trim()}</div>}
        </>
      );
    }
    return <div>{text}</div>;
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black flex flex-col" data-testid="tt-cinematic-chat">
      {/* Backdrop scene */}
      <div className="absolute inset-0">
        {sceneObj ? (
          <img
            src={`data:${sceneObj.mime_type || 'image/png'};base64,${sceneObj.image_base64}`}
            alt={location.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#1a1a2e] via-[#2d1b4e] to-[#1a1a2e]" />
        )}
        {/* Cinematic vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/85" />
        <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black to-transparent" />
        {/* Ambient particles */}
        <AmbientParticles kind={journey.ambience || 'dust'} />
      </div>

      {/* Top bar */}
      <header className="relative z-10 px-3 py-2 flex items-center gap-2 backdrop-blur-sm bg-black/40 border-b border-white/10">
        <button
          onClick={onClose}
          className="px-2 py-1 rounded-full bg-white/10 text-white text-xs hover:bg-white/20 flex items-center gap-1"
          data-testid="tt-cinema-close"
        >
          <X className="w-3.5 h-3.5" /> Lämna
        </button>
        <div className="flex-1 min-w-0 text-center">
          <h3 className="text-white font-bold text-sm leading-tight truncate">{npc.name}</h3>
          <p className="text-white/60 text-[10px] truncate">{location.name} · {journey.era}</p>
        </div>
        <button
          onClick={() => { if (autoTts) stopSpeaking(); setAutoTts(!autoTts); }}
          className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 ${autoTts ? 'bg-amber-300 text-black' : 'bg-white/10 text-white'}`}
          data-testid="tt-cinema-tts-toggle"
          title={autoTts ? 'Auto-uppläsning på' : 'Auto-uppläsning av'}
        >
          {autoTts ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
        </button>
      </header>

      {/* Portrait + bubble area */}
      <div className="relative z-10 flex-1 overflow-y-auto px-3 py-4" ref={scrollRef} data-testid="tt-cinema-messages">
        {/* Portrait */}
        <div className="flex justify-center mb-3">
          {portrait ? (
            <div className="relative">
              <img
                src={`data:${portrait.mime_type || 'image/png'};base64,${portrait.image_base64}`}
                alt={npc.name}
                className="w-32 h-32 rounded-full object-cover border-4 border-amber-200/60 shadow-[0_0_40px_rgba(255,200,100,0.4)]"
              />
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-amber-200 text-black text-[10px] font-bold whitespace-nowrap">
                {npc.emoji} {npc.name.split(' ')[0]}
              </div>
            </div>
          ) : (
            <button
              onClick={generatePortrait}
              disabled={generatingPortrait}
              className="w-32 h-32 rounded-full border-4 border-amber-200/40 bg-black/40 backdrop-blur-sm flex flex-col items-center justify-center text-white/80 text-[11px] hover:bg-black/60 disabled:opacity-60"
              data-testid="tt-cinema-portrait-generate"
            >
              {generatingPortrait ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin mb-1" />
                  <span>Ritar porträtt...</span>
                </>
              ) : (
                <>
                  <span className="text-3xl mb-0.5">{npc.emoji}</span>
                  <span className="font-bold">Visa porträtt</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Greeting (initial) */}
        {npc.greeting && messages.length === 0 && !loading && (
          <div className="max-w-md mx-auto mb-3 px-4 py-3 rounded-2xl bg-black/55 backdrop-blur-md border border-amber-200/20 text-amber-50 text-sm leading-relaxed">
            <div className="text-[10px] italic opacity-60 mb-1">{location.description ? '· ' + location.description.split('.')[0] + ' ·' : ''}</div>
            {npc.greeting}
          </div>
        )}

        {/* Messages */}
        <div className="max-w-md mx-auto space-y-2.5">
          {loading ? (
            <p className="text-center text-white/60 text-sm py-4">Laddar...</p>
          ) : messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              data-testid={`tt-cinema-msg-${i}`}
            >
              <div
                className={`max-w-[88%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed backdrop-blur-md ${
                  m.role === 'user'
                    ? 'bg-rose-300/85 text-black'
                    : 'bg-black/55 border border-amber-200/20 text-amber-50'
                }`}
              >
                {m.role === 'assistant' ? renderAssistant(m.text) : m.text}
                {m.role === 'assistant' && (
                  <button
                    onClick={() => speakingIdx === i ? stopSpeaking() : speakLine(m.text, i)}
                    className="mt-1.5 text-[10px] flex items-center gap-1 opacity-70 hover:opacity-100"
                  >
                    {speakingIdx === i ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                    {speakingIdx === i ? 'Stoppa' : 'Lyssna'}
                  </button>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="px-3 py-2 rounded-2xl bg-black/55 border border-amber-200/20 text-amber-100/70 text-xs italic backdrop-blur-md">
                <span className="inline-block animate-pulse">{npc.name} tänker efter...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input bar */}
      <div className="relative z-10 px-3 py-3 backdrop-blur-md bg-black/55 border-t border-white/10">
        <div className="max-w-md mx-auto flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Säg något till ${npc.name}...`}
            className="flex-1 px-4 py-2.5 rounded-full bg-white/10 backdrop-blur-sm text-white placeholder-white/50 border border-white/20 focus:outline-none focus:border-amber-200/60 text-sm"
            maxLength={800}
            disabled={sending}
            data-testid="tt-cinema-input"
          />
          <button
            onClick={send}
            disabled={sending || !input.trim()}
            className="px-4 py-2.5 rounded-full bg-amber-300 text-black font-bold disabled:opacity-50 hover:bg-amber-200"
            data-testid="tt-cinema-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function NpcChatModal({ journeyId, journey, location, npc, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/timetravel/journeys/${journeyId}/locations/${location.key}/npcs/${npc.key}/messages`,
        { withCredentials: true }
      );
      setMessages(data.messages || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [journeyId, location.key, npc.key]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, sending]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);
    setMessages((prev) => [...prev, { role: 'user', text, at: new Date().toISOString() }]);
    try {
      const { data } = await axios.post(
        `${API}/timetravel/journeys/${journeyId}/locations/${location.key}/npcs/${npc.key}/message`,
        { text }, { withCredentials: true }
      );
      setMessages((prev) => [...prev, data.messages_added[1]]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte svara');
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm p-3 flex items-end md:items-center justify-center"
      onClick={onClose}
      data-testid="tt-npc-chat-modal"
    >
      <div className="w-full max-w-md neo-card bg-white p-3 max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-2 pb-2 border-b-2 border-black/20">
          <span className="text-2xl">{npc.emoji}</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight truncate">{npc.name}</h3>
            <p className="text-[11px] opacity-70 truncate">{npc.role} · {location.name}</p>
          </div>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" aria-label="Stäng" data-testid="tt-npc-chat-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Greeting */}
        {npc.greeting && messages.length === 0 && !loading && (
          <div className="flex justify-start mb-2">
            <div className="px-3 py-2 rounded-2xl border-2 border-black bg-[#FFE8F5] text-sm leading-snug max-w-[85%]">
              <div className="text-[10px] font-bold opacity-70 mb-0.5">{npc.emoji} {npc.name}</div>
              {npc.greeting}
            </div>
          </div>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 mb-2 min-h-[200px]" data-testid="tt-npc-messages">
          {loading ? (
            <p className="text-center text-sm opacity-60 py-4">Laddar samtal...</p>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl border-2 border-black text-sm leading-snug whitespace-pre-wrap ${
                  m.role === 'user' ? 'bg-[#C8B6FF]' : 'bg-[#FFE8F5]'
                }`}>
                  {m.role === 'assistant' && (
                    <div className="text-[10px] font-bold opacity-70 mb-0.5">{npc.emoji} {npc.name}</div>
                  )}
                  {m.text}
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex justify-start">
              <div className="px-3 py-2 rounded-2xl border-2 border-black bg-[#FFE8F5] text-xs italic opacity-70">
                {npc.name} svarar...
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Säg något till ${npc.name}...`}
            className="neo-input flex-1"
            maxLength={800}
            disabled={sending}
            data-testid="tt-npc-input"
          />
          <button
            onClick={send}
            disabled={sending || !input.trim()}
            className="neo-btn px-3 py-2 bg-[#FFC8DD] font-bold disabled:opacity-60"
            data-testid="tt-npc-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function LocationView({ journeyId, journey, location, locations, onBack, onSwitchLocation, onUpdated }) {
  const [scene, setScene] = useState(null);
  const [generatingScene, setGeneratingScene] = useState(false);
  const [activeNpc, setActiveNpc] = useState(null);

  // Load existing scene from journey.scenes if linked
  useEffect(() => {
    if (location.scene_image_id && journey.scenes) {
      const s = journey.scenes.find((sc) => sc.id === location.scene_image_id);
      if (s) setScene(s);
    }
  }, [location.scene_image_id, journey.scenes]);

  const generateScene = async () => {
    setGeneratingScene(true);
    try {
      const { data } = await axios.post(
        `${API}/timetravel/journeys/${journeyId}/locations/${location.key}/scene`,
        {}, { withCredentials: true }
      );
      setScene(data.scene);
      onUpdated?.();
      toast.success('🎨 Scenen är ritad');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte skapa scen');
    } finally {
      setGeneratingScene(false);
    }
  };

  const currentIdx = locations.findIndex((l) => l.key === location.key);
  const prevLoc = currentIdx > 0 ? locations[currentIdx - 1] : null;
  const nextLoc = currentIdx < locations.length - 1 ? locations[currentIdx + 1] : null;

  return (
    <div className="space-y-3" data-testid="tt-location-view">
      <header className="neo-card p-3 bg-gradient-to-br from-[#E8F0FF] to-[#FFE8F5] flex items-center gap-2">
        <button onClick={onBack} className="neo-btn p-1.5 bg-white" aria-label="Tillbaka" data-testid="tt-loc-back">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-2xl">{location.emoji}</span>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm leading-tight truncate">{location.name}</h3>
          <p className="text-[11px] opacity-70 truncate">{journey.location} · {journey.era}</p>
        </div>
      </header>

      {/* Scene */}
      {scene ? (
        <img
          src={`data:${scene.mime_type || 'image/png'};base64,${scene.image_base64}`}
          alt={location.name}
          className="w-full rounded-xl border-[3px] border-black"
        />
      ) : (
        <button
          onClick={generateScene}
          disabled={generatingScene}
          className="w-full neo-btn py-3 bg-[#B9FBC0] font-bold text-sm flex items-center justify-center gap-1 disabled:opacity-60"
          data-testid="tt-loc-generate-scene"
        >
          {generatingScene
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Ritar {location.name}... (15-25s)</>
            : <><ImageIcon className="w-4 h-4" /> Visa {location.name} (AI-bild)</>}
        </button>
      )}

      {/* Description */}
      {location.description && (
        <div className="neo-card p-3 bg-white text-sm italic leading-relaxed" data-testid="tt-loc-description">
          {location.description}
        </div>
      )}

      {/* NPCs */}
      <div>
        <h4 className="font-bold text-sm mb-2 flex items-center gap-1.5">
          <Users className="w-4 h-4" /> Människor här ({location.npcs.length})
        </h4>
        <div className="grid gap-2 grid-cols-1" data-testid="tt-loc-npcs">
          {location.npcs.map((npc) => (
            <button
              key={npc.key}
              onClick={() => setActiveNpc(npc)}
              className="neo-card p-3 bg-white text-left hover:bg-[#FFE8F5] transition flex items-start gap-2"
              data-testid={`tt-npc-${npc.key}`}
            >
              <span className="text-2xl shrink-0">{npc.emoji}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <h5 className="font-bold text-sm">{npc.name}</h5>
                  <span className="text-[10px] opacity-60 truncate">{npc.role}</span>
                </div>
                <p className="text-[11px] opacity-75 leading-snug line-clamp-2 mt-0.5">{npc.personality}</p>
              </div>
              <Send className="w-4 h-4 shrink-0 mt-1 opacity-60" />
            </button>
          ))}
        </div>
      </div>

      {/* Navigate to other places */}
      <div className="flex gap-2">
        <button
          onClick={() => prevLoc && onSwitchLocation(prevLoc)}
          disabled={!prevLoc}
          className="neo-btn flex-1 py-2 bg-white font-bold text-xs flex items-center justify-center gap-1 disabled:opacity-40"
          data-testid="tt-loc-prev"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {prevLoc ? `${prevLoc.emoji} ${prevLoc.name}` : 'Inga fler'}
        </button>
        <button
          onClick={() => nextLoc && onSwitchLocation(nextLoc)}
          disabled={!nextLoc}
          className="neo-btn flex-1 py-2 bg-[#FFC8DD] font-bold text-xs flex items-center justify-center gap-1 disabled:opacity-40"
          data-testid="tt-loc-next"
        >
          {nextLoc ? `${nextLoc.emoji} ${nextLoc.name}` : 'Inga fler'}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {activeNpc && (
        <CinematicNpcChat
          journeyId={journeyId}
          journey={journey}
          location={location}
          npc={activeNpc}
          sceneObj={scene}
          onClose={() => setActiveNpc(null)}
        />
      )}
    </div>
  );
}

function ExploreLoadingView({ journey, onClose }) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const messages = [
    { at: 0,  emoji: '🗺️', text: 'Förbereder världen...' },
    { at: 5,  emoji: '🏛️', text: `Letar efter rum i ${journey.location}...` },
    { at: 12, emoji: '👥', text: `Möter människor från ${journey.era}...` },
    { at: 20, emoji: '✨', text: 'Skriver personligheter och samtalsöppningar...' },
    { at: 28, emoji: '🌸', text: 'Snart klart — det här är värt väntan...' },
    { at: 40, emoji: '⏳', text: 'AI:n jobbar fortfarande, vänta lite till...' },
  ];
  const current = [...messages].reverse().find((m) => secs >= m.at) || messages[0];
  const pct = Math.min(95, (secs / 35) * 100);

  return (
    <div className="space-y-3" data-testid="tt-explore-loading">
      <header className="neo-card p-3 bg-gradient-to-br from-[#E8F0FF] to-[#FFE8F5] flex items-center gap-2">
        <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="tt-explore-close">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <Map className="w-4 h-4" />
        <h3 className="font-bold text-sm">Utforska {journey.location}</h3>
      </header>
      <div className="neo-card p-6 bg-white text-center space-y-4">
        <div className="text-6xl animate-bounce">{current.emoji}</div>
        <div>
          <p className="text-base font-bold font-['Fredoka']">{current.text}</p>
          <p className="text-xs opacity-60 mt-1">
            {secs}s av ungefär 25-35 sekunder
          </p>
        </div>
        {/* Progress bar */}
        <div className="w-full h-3 rounded-full border-2 border-black bg-white overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#FFC8DD] via-[#C8B6FF] to-[#A2D2FF] transition-all duration-1000"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="text-[11px] opacity-50 italic max-w-xs mx-auto leading-relaxed">
          AI:n skapar 4-6 platser med 2-3 människor på varje plats — varje med sin egen personlighet och bakgrund. Det här är inte en mall, allt är unikt för just din resa 💛
        </p>
      </div>
    </div>
  );
}

function ExploreView({ journey, journeyId, onClose, onUpdated }) {
  const [locations, setLocations] = useState(journey.locations || null);
  const [loading, setLoading] = useState(!journey.locations || journey.locations.length === 0);
  const [activeLoc, setActiveLoc] = useState(null);

  const generate = useCallback(async () => {
    setLoading(true);
    let cancelled = false;

    const pollUntilReady = async () => {
      // Poll every 4 seconds for up to 3 minutes
      for (let i = 0; i < 45; i++) {
        if (cancelled) return;
        await new Promise((r) => setTimeout(r, 4000));
        try {
          const { data } = await axios.get(
            `${API}/timetravel/journeys/${journeyId}`,
            { withCredentials: true, timeout: 10000 }
          );
          if (cancelled) return;
          if (data.explore_status === 'error') {
            toast.error(data.explore_error || 'Kunde inte bygga världen — försök igen');
            setLoading(false);
            return;
          }
          if ((data.locations || []).length >= 3) {
            setLocations(data.locations);
            setLoading(false);
            onUpdated?.();
            return;
          }
        } catch {
          // ignore transient network errors during polling — try again next tick
        }
      }
      if (!cancelled) {
        toast.error('Tar längre tid än vanligt — vänta lite till och prova igen');
        setLoading(false);
      }
    };

    try {
      const { data } = await axios.post(
        `${API}/timetravel/journeys/${journeyId}/explore`, {},
        { withCredentials: true, timeout: 15000 }
      );
      if (data.status === 'ready' && data.locations) {
        setLocations(data.locations);
        setLoading(false);
        onUpdated?.();
        return;
      }
      // status === 'processing' — start polling
      pollUntilReady();
    } catch (e) {
      // If POST itself failed (e.g. brief network blip), still try polling — backend may have started anyway
      pollUntilReady();
    }

    return () => { cancelled = true; };
  }, [journeyId, onUpdated]);

  useEffect(() => {
    if (!locations || locations.length === 0) generate();
  }, [locations, generate]);

  if (loading || !locations) {
    return <ExploreLoadingView journey={journey} onClose={onClose} />;
  }

  if (activeLoc) {
    return (
      <LocationView
        journeyId={journeyId}
        journey={{ ...journey, locations }}
        location={activeLoc}
        locations={locations}
        onBack={() => setActiveLoc(null)}
        onSwitchLocation={(loc) => setActiveLoc(loc)}
        onUpdated={onUpdated}
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="tt-explore-view">
      <header className="neo-card p-3 bg-gradient-to-br from-[#E8F0FF] to-[#FFE8F5] flex items-center gap-2">
        <button onClick={onClose} className="neo-btn p-1.5 bg-white" aria-label="Tillbaka" data-testid="tt-explore-close">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <Map className="w-4 h-4" />
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm leading-tight truncate">Utforska {journey.location}</h3>
          <p className="text-[11px] opacity-70 truncate">{journey.era} · Klicka en plats för att gå dit</p>
        </div>
      </header>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2" data-testid="tt-locations-grid">
        {locations.map((loc) => (
          <button
            key={loc.key}
            onClick={() => setActiveLoc(loc)}
            className="neo-card p-3 bg-white hover:bg-[#FFE8F5] transition text-left flex flex-col gap-2"
            data-testid={`tt-location-card-${loc.key}`}
          >
            <div className="flex items-start gap-2">
              <span className="text-3xl shrink-0">{loc.emoji}</span>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm leading-tight">{loc.name}</h4>
                <p className="text-[11px] opacity-75 leading-snug line-clamp-3 mt-0.5">{loc.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-wrap pt-1 border-t border-black/10">
              <Users className="w-3 h-3 opacity-60" />
              {loc.npcs.slice(0, 4).map((n) => (
                <span key={n.key} className="text-[14px]" title={n.name}>{n.emoji}</span>
              ))}
              <span className="text-[10px] opacity-60 ml-1">{loc.npcs.length} personer</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function JourneyView({ journeyId, onBack, onDeleted }) {
  const [j, setJ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [generatingScene, setGeneratingScene] = useState(false);
  const [showFacts, setShowFacts] = useState(true);
  const [activeScene, setActiveScene] = useState(null);
  const [showDayInLife, setShowDayInLife] = useState(false);
  const [showExplore, setShowExplore] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/timetravel/journeys/${journeyId}`, { withCredentials: true });
      setJ(data);
    } catch {
      toast.error('Kunde inte ladda tidsresan');
    } finally {
      setLoading(false);
    }
  }, [journeyId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [j?.messages?.length]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);
    // Optimistic
    setJ((prev) => prev ? { ...prev, messages: [...prev.messages, { role: 'user', text, at: new Date().toISOString() }] } : prev);
    try {
      const { data } = await axios.post(
        `${API}/timetravel/journeys/${journeyId}/message`,
        { text }, { withCredentials: true }
      );
      setJ((prev) => prev ? { ...prev, messages: [...prev.messages, data.messages_added[1]] } : prev);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte skicka');
      // revert optimistic
      setJ((prev) => prev ? { ...prev, messages: prev.messages.slice(0, -1) } : prev);
    } finally {
      setSending(false);
    }
  };

  const generateScene = async (description) => {
    setGeneratingScene(true);
    try {
      const { data } = await axios.post(
        `${API}/timetravel/journeys/${journeyId}/scene`,
        { description: description || null }, { withCredentials: true }
      );
      setJ((prev) => prev ? { ...prev, scenes: [...(prev.scenes || []), data.scene] } : prev);
      setActiveScene(data.scene);
      toast.success('🎨 Scenen är ritad');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte skapa scen');
    } finally {
      setGeneratingScene(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Ta bort hela tidsresan?')) return;
    try {
      await axios.delete(`${API}/timetravel/journeys/${journeyId}`, { withCredentials: true });
      onDeleted();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  if (loading || !j) {
    return <div className="neo-card p-8 bg-white text-center">Laddar tidsresan...</div>;
  }

  if (showDayInLife) {
    return (
      <DayInLifeView
        journey={j}
        onClose={() => { setShowDayInLife(false); load(); }}
        onUpdated={load}
      />
    );
  }

  if (showExplore) {
    return (
      <ExploreView
        journey={j}
        journeyId={journeyId}
        onClose={() => { setShowExplore(false); load(); }}
        onUpdated={load}
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="tt-journey-view">
      <header className="neo-card p-3 bg-gradient-to-br from-[#FDF0D5] to-[#FFE8F5] flex items-center gap-2">
        <button onClick={onBack} className="neo-btn p-1.5 bg-white" aria-label="Tillbaka" data-testid="tt-back">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-2xl">{j.emoji || '🕰️'}</span>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm leading-tight truncate">{j.title}</h3>
          <p className="text-[11px] opacity-70 flex items-center gap-2 truncate">
            <span><MapPin className="w-3 h-3 inline" /> {j.location}</span>
            <span>·</span>
            <span><User className="w-3 h-3 inline" /> {j.character}</span>
          </p>
        </div>
        <button onClick={remove} className="neo-btn p-1.5 bg-white text-red-600" aria-label="Ta bort" data-testid="tt-journey-delete">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </header>

      {/* Three facts (Lär mig 3 saker) */}
      {showFacts && j.three_facts && j.three_facts.length > 0 && (
        <div className="neo-card p-3 bg-[#FFF7B5]" data-testid="tt-three-facts">
          <div className="flex items-center justify-between mb-1.5">
            <h4 className="font-bold text-xs flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" /> Lär dig 3 saker innan resan
            </h4>
            <button onClick={() => setShowFacts(false)} className="text-xs opacity-60 hover:opacity-100" data-testid="tt-hide-facts">Stäng</button>
          </div>
          <ul className="space-y-1">
            {j.three_facts.map((f, i) => (
              <li key={i} className="text-xs leading-snug flex gap-1.5">
                <span className="font-bold">{i + 1}.</span> <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Hint card */}
      {j.hint && (
        <div className="neo-card p-3 bg-white text-xs italic opacity-80" data-testid="tt-hint">
          <span className="font-bold not-italic">Just nu:</span> {j.hint}
        </div>
      )}

      {/* Scenes gallery */}
      {(j.scenes || []).length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="tt-scenes-row">
          {j.scenes.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveScene(s)}
              className="shrink-0 neo-card p-1 bg-white"
              data-testid={`tt-scene-${s.id}`}
            >
              <img
                src={`data:${s.mime_type || 'image/png'};base64,${s.image_base64}`}
                alt={s.description || 'Scen'}
                className="w-24 h-24 object-cover rounded-md"
              />
            </button>
          ))}
        </div>
      )}

      {/* Chat */}
      <div ref={scrollRef} className="neo-card p-3 bg-white max-h-[55vh] overflow-y-auto space-y-2" data-testid="tt-messages">
        {(j.messages || []).length === 0 ? (
          <div className="text-center text-sm opacity-60 py-6">
            Säg hej till {j.character} för att börja samtalet 💛
          </div>
        ) : (
          j.messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              data-testid={`tt-msg-${i}`}
            >
              <div
                className={`max-w-[85%] px-3 py-2 rounded-2xl border-2 border-black text-sm leading-snug whitespace-pre-wrap ${
                  m.role === 'user' ? 'bg-[#C8B6FF]' : 'bg-[#FFE8F5]'
                }`}
              >
                {m.role === 'assistant' && (
                  <div className="text-[10px] font-bold opacity-70 mb-0.5">{j.emoji} {j.character}</div>
                )}
                {m.text}
              </div>
            </div>
          ))
        )}
        {sending && (
          <div className="flex justify-start">
            <div className="px-3 py-2 rounded-2xl border-2 border-black bg-[#FFE8F5] text-xs italic opacity-70">
              {j.character} skriver...
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={`Skriv något till ${j.character}...`}
          className="neo-input flex-1"
          maxLength={800}
          disabled={sending}
          data-testid="tt-msg-input"
        />
        <button
          onClick={send}
          disabled={sending || !input.trim()}
          className="neo-btn px-3 py-2 bg-[#FFC8DD] font-bold disabled:opacity-60"
          data-testid="tt-msg-send"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>

      {/* Scene generation */}
      <button
        onClick={() => generateScene(null)}
        disabled={generatingScene}
        className="neo-btn w-full py-2 bg-[#B9FBC0] font-bold text-sm flex items-center justify-center gap-1 disabled:opacity-60"
        data-testid="tt-generate-scene"
      >
        {generatingScene
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Ritar scenen... (10-20 sek)</>
          : <><ImageIcon className="w-4 h-4" /> Visa hur det ser ut här (AI-bild)</>}
      </button>

      {/* Day in life */}
      <button
        onClick={() => setShowDayInLife(true)}
        className="neo-btn w-full py-2 bg-gradient-to-br from-[#FFF7B5] to-[#FFE8F5] font-bold text-sm flex items-center justify-center gap-1"
        data-testid="tt-open-day-in-life"
      >
        <Sun className="w-4 h-4" /> En dag i livet — låt {j.character} berätta
      </button>

      {/* Explore (walk around / talk to people) */}
      <button
        onClick={() => setShowExplore(true)}
        className="neo-btn w-full py-2 bg-gradient-to-br from-[#E8F0FF] to-[#C8B6FF] font-bold text-sm flex items-center justify-center gap-1"
        data-testid="tt-open-explore"
      >
        <Map className="w-4 h-4" /> Gå runt här — möt fler människor
      </button>

      {/* Active scene modal */}
      {activeScene && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm p-3 flex items-center justify-center"
          onClick={() => setActiveScene(null)}
          data-testid="tt-scene-modal"
        >
          <div className="max-w-2xl w-full neo-card bg-white p-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-sm">🎨 Scen från {j.era}</h4>
              <button onClick={() => setActiveScene(null)} className="neo-btn p-1.5 bg-white" data-testid="tt-scene-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <img
              src={`data:${activeScene.mime_type || 'image/png'};base64,${activeScene.image_base64}`}
              alt={activeScene.description}
              className="w-full rounded-xl border-2 border-black"
            />
            <p className="text-xs mt-2 opacity-70">{activeScene.description}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TimeTravelPage() {
  const [presets, setPresets] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const fetchPresets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/timetravel/presets`, { withCredentials: true });
      setPresets(data.presets || []);
    } catch { /* ignore */ }
  }, []);

  const fetchJourneys = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/timetravel/journeys`, { withCredentials: true });
      setJourneys(data.journeys || []);
    } catch {
      toast.error('Kunde inte hämta dina resor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPresets(); fetchJourneys(); }, [fetchPresets, fetchJourneys]);

  const startJourney = async (body) => {
    try {
      const { data } = await axios.post(`${API}/timetravel/journeys`, body, { withCredentials: true });
      toast.success('🕰️ Tidsresan har börjat');
      setActiveId(data.id);
      fetchJourneys();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte starta resan');
    }
  };

  const startRandom = async () => {
    try {
      const { data } = await axios.post(`${API}/timetravel/random`, {}, { withCredentials: true });
      const preset = data.preset;
      if (preset?.key) {
        toast(`🎲 Resa till: ${preset.title}`);
        await startJourney({ preset_key: preset.key });
      }
    } catch {
      toast.error('Kunde inte slumpa');
    }
  };

  const deleteJourney = async (id) => {
    if (!window.confirm('Ta bort tidsresan?')) return;
    try {
      await axios.delete(`${API}/timetravel/journeys/${id}`, { withCredentials: true });
      fetchJourneys();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  if (activeId) {
    return (
      <JourneyView
        journeyId={activeId}
        onBack={() => { setActiveId(null); fetchJourneys(); }}
        onDeleted={() => { setActiveId(null); fetchJourneys(); }}
      />
    );
  }

  return (
    <HomeView
      presets={presets}
      onStart={startJourney}
      onRandom={startRandom}
      journeys={journeys}
      onOpen={setActiveId}
      onDelete={deleteJourney}
      loading={loading}
    />
  );
}
